import React, { useCallback, useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import {
  ArrowDownTrayIcon,
  ArrowPathIcon,
  DocumentTextIcon,
  ExclamationTriangleIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import { apiService } from "../services/apiService";

export interface InvoicePdfPreviewModalProps {
  invoiceId: string;
  invoiceNumber?: string | number;
  open: boolean;
  onClose: () => void;
}

export const InvoicePdfPreviewModal: React.FC<InvoicePdfPreviewModalProps> = ({
  invoiceId,
  invoiceNumber,
  open,
  onClose,
}) => {
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const blobRef = useRef<Blob | null>(null);
  const pdfUrlRef = useRef<string | null>(null);

  const revokePdfUrl = useCallback(() => {
    if (pdfUrlRef.current) {
      URL.revokeObjectURL(pdfUrlRef.current);
      pdfUrlRef.current = null;
    }
    setPdfUrl(null);
  }, []);

  const loadPdf = useCallback(async () => {
    setLoading(true);
    setError(null);
    revokePdfUrl();
    blobRef.current = null;

    const result = await apiService.fetchInvoicePdf(invoiceId);
    if (result.error || !result.blob) {
      setError(result.message || result.error || "No se pudo generar el PDF");
      setLoading(false);
      return;
    }
    blobRef.current = result.blob;
    const url = URL.createObjectURL(result.blob);
    pdfUrlRef.current = url;
    setPdfUrl(url);
    setLoading(false);
  }, [invoiceId, revokePdfUrl]);

  useEffect(() => {
    if (!open || !invoiceId) return undefined;
    loadPdf();
    return () => revokePdfUrl();
  }, [open, invoiceId, loadPdf, revokePdfUrl]);

  useEffect(() => {
    if (!open) {
      revokePdfUrl();
      blobRef.current = null;
      setError(null);
      setLoading(true);
    }
  }, [open, revokePdfUrl]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  const handleDownload = async () => {
    setDownloading(true);
    try {
      if (blobRef.current) {
        const url = URL.createObjectURL(blobRef.current);
        const link = document.createElement("a");
        link.href = url;
        link.download = `factura-${invoiceNumber ?? invoiceId}.pdf`;
        link.click();
        URL.revokeObjectURL(url);
        return;
      }
      const result = await apiService.downloadInvoicePdf(
        invoiceId,
        `factura-${invoiceNumber ?? invoiceId}.pdf`
      );
      if (!result.ok) {
        setError(result.message || result.error || "No se pudo descargar el PDF");
      }
    } finally {
      setDownloading(false);
    }
  };

  if (!open) return null;

  return (
    <AnimatePresence>
      <motion.div
        className="fixed inset-0 z-[70] flex items-center justify-center p-4 sm:p-6"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        aria-modal
        role="dialog"
      >
        <motion.button
          type="button"
          className="absolute inset-0 bg-black/40 backdrop-blur-sm"
          aria-label="Cerrar vista previa"
          onClick={onClose}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        />

        <motion.div
          className="relative flex w-full max-w-5xl flex-col overflow-hidden rounded-2xl border border-[#E6E9F0] bg-white shadow-stripe-lg dark:border-[#21262d] dark:bg-[#161b22]"
          initial={{ opacity: 0, y: 24, scale: 0.97 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 16, scale: 0.98 }}
          transition={{ type: "spring", stiffness: 380, damping: 32 }}
          style={{ maxHeight: "min(92vh, 900px)" }}
        >
          <header className="flex shrink-0 items-center justify-between gap-3 border-b border-[#E6E9F0] px-5 py-4 dark:border-[#21262d]">
            <div className="flex min-w-0 items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-[#635BFF]/10 text-[#635BFF]">
                <DocumentTextIcon className="h-5 w-5" />
              </div>
              <div className="min-w-0">
                <h2 className="truncate text-base font-semibold tracking-tight text-[#0A2540] dark:text-[#e6edf3]">
                  Vista previa PDF
                </h2>
                <p className="truncate text-sm text-[#8898aa]">
                  Factura #{invoiceNumber ?? "—"} · mismo documento que se adjunta al email
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={handleDownload}
                disabled={loading || !!error || downloading}
                className="btn-stripe-secondary disabled:cursor-not-allowed disabled:opacity-40"
              >
                <ArrowDownTrayIcon className="h-4 w-4" />
                {downloading ? "Descargando…" : "Descargar PDF"}
              </button>
              <button
                type="button"
                onClick={onClose}
                className="rounded-lg p-2 text-[#8898aa] transition hover:bg-[#F6F9FC] hover:text-[#425466] dark:hover:bg-[#21262d] dark:hover:text-[#e6edf3]"
                aria-label="Cerrar"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>
          </header>

          <div className="relative min-h-0 flex-1 bg-[#F6F9FC] dark:bg-[#0d1117]">
            {loading && (
              <div className="absolute inset-0 flex flex-col items-center justify-center gap-4">
                <div className="h-10 w-10 rounded-full border-2 border-[#635BFF] border-t-transparent animate-spin" />
                <p className="text-sm text-[#8898aa]">Generando PDF…</p>
              </div>
            )}

            {!loading && error && (
              <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 px-6 text-center">
                <ExclamationTriangleIcon className="h-10 w-10 text-amber-500" />
                <p className="text-sm font-medium text-[#0A2540] dark:text-[#e6edf3]">{error}</p>
                <div className="mt-2 flex flex-wrap items-center justify-center gap-2">
                  <button type="button" onClick={loadPdf} className="btn-stripe-secondary">
                    <ArrowPathIcon className="h-4 w-4" />
                    Reintentar
                  </button>
                  <button type="button" onClick={onClose} className="btn-stripe-primary">
                    Cerrar
                  </button>
                </div>
              </div>
            )}

            {!loading && !error && pdfUrl && (
              <iframe
                title={`Vista previa factura ${invoiceNumber ?? invoiceId}`}
                src={`${pdfUrl}#toolbar=1&navpanes=0`}
                className="h-full min-h-[480px] w-full border-0 bg-white"
              />
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};
